import { db } from './firebase';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';

export interface Rating {
  id?: string;
  userId: string;
  mediaId: number;
  mediaType: 'movie' | 'tv';
  rating: number; // 1-10 scale
  createdAt: any;
  updatedAt: any;
  // Optional metadata
  mediaTitle?: string;
  posterPath?: string;
}

const RATINGS_COLLECTION = 'ratings';

export const ratingsService = {
  // Get user's rating for a specific movie/show
  async getUserRating(userId: string, mediaId: number, mediaType: 'movie' | 'tv'): Promise<Rating | null> {
    try {
      const ratingId = `${mediaType}_${mediaId}`;
      const userRatingsRef = collection(db, 'users', userId, RATINGS_COLLECTION);
      const ratingDoc = await getDoc(doc(userRatingsRef, ratingId));

      if (ratingDoc.exists()) {
        return { id: ratingDoc.id, ...ratingDoc.data() } as Rating;
      }
      return null;
    } catch (error) {
      return null;
    }
  },

  // Set/update user's rating for a movie/show
  async setUserRating(
    userId: string,
    mediaId: number,
    mediaType: 'movie' | 'tv',
    rating: number,
    metadata?: { mediaTitle?: string; posterPath?: string }
  ): Promise<boolean> {
    try {
      const ratingId = `${mediaType}_${mediaId}`;
      const ratingData: Omit<Rating, 'id'> = {
        userId,
        mediaId,
        mediaType,
        rating,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...metadata
      };

      // First ensure the user document exists
      await setDoc(doc(db, 'users', userId), {
        lastActive: serverTimestamp()
      }, { merge: true });

      const userRatingsRef = collection(db, 'users', userId, RATINGS_COLLECTION);
      await setDoc(doc(userRatingsRef, ratingId), ratingData, { merge: true });
      return true;
    } catch (error) {
      return false;
    }
  },

  // Remove user's rating for a movie/show
  async removeUserRating(userId: string, mediaId: number, mediaType: 'movie' | 'tv'): Promise<boolean> {
    try {
      const ratingId = `${mediaType}_${mediaId}`;
      await deleteDoc(doc(db, 'users', userId, RATINGS_COLLECTION, ratingId));
      return true;
    } catch (error) {
      return false;
    }
  },

  // Get all ratings for a user
  async getUserRatings(userId: string): Promise<Rating[]> {
    try {
      const ratingsSnapshot = await getDocs(collection(db, 'users', userId, RATINGS_COLLECTION));
      return ratingsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Rating[];
    } catch (error) {
      return [];
    }
  },

  // Get user's ratings by type (movies or tv shows)
  async getUserRatingsByType(userId: string, mediaType: 'movie' | 'tv'): Promise<Rating[]> {
    try {
      const q = query(
        collection(db, 'users', userId, RATINGS_COLLECTION),
        where('mediaType', '==', mediaType)
      );
      const ratingsSnapshot = await getDocs(q);
      return ratingsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Rating[];
    } catch (error) {
      return [];
    }
  },

  // Get user's top rated items
  async getTopRatedItems(userId: string, limit: number = 10): Promise<Rating[]> {
    try {
      const ratings = await this.getUserRatings(userId);
      return ratings
        .sort((a, b) => b.rating - a.rating)
        .slice(0, limit);
    } catch (error) {
      return [];
    }
  },

  // Get rating statistics for a user
  async getRatingStats(userId: string): Promise<{
    totalRatings: number;
    averageRating: number;
    movieRatings: number;
    tvRatings: number;
    highestRating: number;
    lowestRating: number;
  }> {
    try {
      const ratings = await this.getUserRatings(userId);

      if (ratings.length === 0) {
        return {
          totalRatings: 0,
          averageRating: 0,
          movieRatings: 0,
          tvRatings: 0,
          highestRating: 0,
          lowestRating: 0
        };
      }

      const movieRatings = ratings.filter(r => r.mediaType === 'movie').length;
      const tvRatings = ratings.filter(r => r.mediaType === 'tv').length;
      const ratingValues = ratings.map(r => r.rating);
      const averageRating = ratingValues.reduce((sum, rating) => sum + rating, 0) / ratingValues.length;

      return {
        totalRatings: ratings.length,
        averageRating: Math.round(averageRating * 10) / 10, // Round to 1 decimal
        movieRatings,
        tvRatings,
        highestRating: Math.max(...ratingValues),
        lowestRating: Math.min(...ratingValues)
      };
    } catch (error) {
      return {
        totalRatings: 0,
        averageRating: 0,
        movieRatings: 0,
        tvRatings: 0,
        highestRating: 0,
        lowestRating: 0
      };
    }
  }
};